import { useQuery } from "@tanstack/react-query"
import { KeyRound } from "lucide-react"

import { api } from "@/api/cliente"
import { CampoDeFicha } from "@/componentes/personas/CampoDeFicha"
import type { Editor, Persona } from "@/componentes/personas/persona"
import { FECHA_MAXIMA, FECHA_MINIMA } from "@/lib/fechas"
import { PAIS_POR_DEFECTO, PAISES } from "@/lib/paises"

/**
 * Su puesto y su cuenta.
 *
 * ⚠ **El acceso al sistema es OPCIONAL.** Una persona existe sin cuenta —la mayoría
 * de la plantilla no entra al sistema— y una cuenta pertenece a una sola ficha. El
 * selector ofrece únicamente las libres: ofrecer una ya tomada sería ofrecer un 409,
 * porque `usuario_id` es único en la base.
 *
 * ⚠ **Desvincular no borra la cuenta ni la ficha.** Sólo deshace la correspondencia
 * entre las dos, que es lo que hace falta cuando alguien cambia de cuenta.
 *
 * ⚠ **El cargo sale del catálogo, no de texto libre.** Con texto libre «Tramitador»
 * y «tramitador» eran dos puestos para la base y el mismo para las personas.
 */
type CargoDeFicha = {
  id: number
  nombre: string
  activo: boolean
  departamento_id: number | null
  departamento_nombre: string | null
  departamentos_adicionales?: number[]
}

export function VinculoDePersona({ persona, valor, poner }: { persona: Persona } & Editor) {
  const esEmpleado = persona.tipo !== "CLIENTE"

  /**
   * Las cuentas a las que se puede enlazar.
   *
   * ⚠ **`incluir` es la suya.** El endpoint devuelve sólo las libres, y la de esta
   * ficha no lo está: sin este parámetro, su propio enlace desaparecería del
   * selector nada más abrirla y parecería que no tiene ninguna.
   */
  const cuentas = useQuery({
    queryKey: ["cuentas-libres", persona.usuario_id],
    queryFn: () => {
      const p = new URLSearchParams()
      if (persona.usuario_id !== null) p.set("incluir", String(persona.usuario_id))
      return api.get<{ id: number; nombre: string; email: string }[]>(
        `/api/v1/personas/cuentas-libres?${p}`,
      )
    },
  })

  const cargos = useQuery({
    queryKey: ["cargos"],
    queryFn: () => api.get<CargoDeFicha[]>("/api/v1/cargos"),
    enabled: esEmpleado,
  })

  const enlazada = valor("usuario_id") !== ""
  // (28/10) El cargo elegido y, si está en varios departamentos (la excepción), cuáles.
  const elegido = (cargos.data ?? []).find((c) => String(c.id) === valor("cargo_id"))
  const adicionales = elegido?.departamentos_adicionales ?? []
  const departamentos = useQuery({
    queryKey: ["departamentos"],
    queryFn: () => api.get<{ id: number; nombre: string }[]>("/api/v1/departamentos"),
    enabled: adicionales.length > 0,
  })

  return (
    <>
      {/* Sólo para empleados: un contacto de importador no tiene cargo ni ingreso, y
          enseñar esos campos vacíos invita a rellenarlos con algo. */}
      {esEmpleado && (
        <section>
          <h3 className="rotulo mb-3">Vinculación</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <CampoDeFicha id="p-cargo" etiqueta="Cargo">
              {/* Se ofrecen sólo los activos —lo retirado no se elige— y el suyo
                  aunque lo esté, para que no desaparezca de su propia ficha. */}
              <select
                id="p-cargo"
                className="campo"
                data-testid="cargo-persona"
                value={valor("cargo_id")}
                onChange={(e) => poner("cargo_id", e.target.value)}
              >
                <option value="">Sin cargo</option>
                {(cargos.data ?? [])
                  .filter((c) => c.activo || c.id === persona.cargo_id)
                  .map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.nombre}
                      {c.activo ? "" : " (retirado)"}
                    </option>
                  ))}
              </select>
              {cargos.data?.length === 0 && (
                <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                  No hay cargos en el catálogo todavía. Se crean en Planificación estratégica ›
                  Cargos.
                </p>
              )}
            </CampoDeFicha>
            {elegido && adicionales.length > 0 && (
              <CampoDeFicha id="p-departamento" etiqueta="Departamento">
                {/* El cargo está en varios departamentos: aquí se dice en cuál trabaja
                    ella. Vacío es el principal del cargo. */}
                <select
                  id="p-departamento"
                  className="campo"
                  value={valor("departamento_id")}
                  onChange={(e) => poner("departamento_id", e.target.value)}
                >
                  <option value="">{elegido.departamento_nombre ?? "El principal"}</option>
                  {(departamentos.data ?? [])
                    .filter((d) => adicionales.includes(d.id))
                    .map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {d.nombre}
                      </option>
                    ))}
                </select>
              </CampoDeFicha>
            )}
            {/* ⚠ **El corporativo, aparte del particular de «Contacto».** Es el
                que sale impreso en la firma de correo, y esa tarjeta se pega al pie
                de los correos que van a clientes: el móvil de alguien no tiene por
                qué acabar ahí.

                ⚠ **Vacío no es un hueco: significa «sale el de la empresa».** Quien
                no tiene extensión propia va con la centralita, que es lo correcto. */}
            <CampoDeFicha id="p-tel-corp" etiqueta="Teléfono corporativo">
              <div className="flex gap-2">
                <select
                  aria-label="País del teléfono corporativo"
                  className="campo w-32 shrink-0"
                  data-testid="pais-telefono"
                  value={valor("pais_telefono") || PAIS_POR_DEFECTO}
                  onChange={(e) => poner("pais_telefono", e.target.value)}
                >
                  {PAISES.map((pa) => (
                    <option key={pa.iso} value={pa.iso}>
                      {pa.prefijo} · {pa.nombre}
                    </option>
                  ))}
                </select>
                <input
                  id="p-tel-corp"
                  className="campo flex-1 tabular-nums"
                  placeholder="241 823 35 60"
                  value={valor("telefono_corporativo")}
                  onChange={(e) => poner("telefono_corporativo", e.target.value)}
                />
              </div>
              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                Si lo dejas vacío, la firma sale con el de la empresa.
              </p>
            </CampoDeFicha>
            <CampoDeFicha id="p-ingreso" etiqueta="Fecha de ingreso">
              <input
                id="p-ingreso"
                type="date"
                className="campo"
                min={FECHA_MINIMA}
                max={FECHA_MAXIMA}
                value={valor("fecha_ingreso")}
                onChange={(e) => poner("fecha_ingreso", e.target.value)}
              />
            </CampoDeFicha>
          </div>
        </section>
      )}

      <section>
        <h3 className="rotulo mb-3">Acceso al sistema</h3>
        <div className="superficie p-3">
          <div className="flex items-start gap-3">
            <span
              aria-hidden={true}
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]"
              style={{
                color: enlazada ? "var(--primary-texto)" : "var(--fg-muted)",
                backgroundColor: enlazada ? "var(--primary-suave)" : "var(--surface-elevated)",
              }}
            >
              <KeyRound size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <CampoDeFicha id="p-usuario" etiqueta="Cuenta enlazada">
                <select
                  id="p-usuario"
                  className="campo"
                  data-testid="cuenta-persona"
                  value={valor("usuario_id")}
                  onChange={(e) => poner("usuario_id", e.target.value)}
                >
                  {/* «Sin cuenta» es la primera y es un valor legítimo, no un hueco:
                      la mayoría de la plantilla no entra al sistema. */}
                  <option value="">Sin cuenta — no entra al sistema</option>
                  {(cuentas.data ?? []).map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.nombre} · {c.email}
                    </option>
                  ))}
                </select>
              </CampoDeFicha>
              <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
                {enlazada
                  ? "Desvincular no borra la cuenta ni la ficha: sólo deshace la correspondencia."
                  : "Sólo se ofrecen las cuentas internas que no tiene ya otra ficha."}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
